import { useRef, useEffect, useMemo } from 'react'

/**
 * A config to control the hover behavior on the dropdown trigger.
 * You can pass true to enable hover with default delays.
 * Or you can pass an object with delays in milliseconds:
 *   - openDelay: delay before showing the dropdown on hover (default 0ms)
 *   - closeDelay: delay before hiding the dropdown (default 150ms). The hide delay
 *     is needed to allow users to move the mouse from the trigger to the dropdown content when
 *     there are gaps between them.
 */
export type ShowOnHoverConfig =
  | boolean
  | { openDelay: number; closeDelay?: number }
  | { openDelay?: number; closeDelay: number }

// 150 ms is a good default delay to allow moving the mouse to the dropdown content
const DEFAULT_OPEN_DELAY = 0
const DEFAULT_CLOSE_DELAY = 150

interface UseHoverDelaysConfig {
  showOnHover: ShowOnHoverConfig
  onShow: () => void
  onHide: () => void
}

interface UseHoverDelaysReturn {
  isHoverEnabled: boolean
  handleTriggerMouseEnter: () => void
  handleTriggerMouseLeave: () => void
  handleContentMouseEnter: () => void
  handleContentMouseLeave: () => void
  clearAllTimeouts: () => void
}

/**
 * Hook that manages hover delays for dropdowns.
 */
export function useShowOnHover({
  showOnHover,
  onShow,
  onHide,
}: UseHoverDelaysConfig): UseHoverDelaysReturn {
  // Use ref as we need to be able to check in real time if the user is hovering the opened "content"
  // directly from event handlers.
  const isHoveringContentRef = useRef(false)

  const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { isHoverEnabled, openDelay, closeDelay } =
    useParseShowOnHoverConfig(showOnHover)

  const handleTriggerMouseEnter = () => {
    if (openTimeout.current !== null) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }

    if (closeTimeout.current !== null) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }

    openTimeout.current = setTimeout(() => {
      onShow()
    }, openDelay)
  }

  const handleTriggerMouseLeave = () => {
    // Clear any pending open timeout if user leaves before delay completes
    if (openTimeout.current !== null) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }

    // setTimeout so the user can actually reach the dropdown content
    closeTimeout.current = setTimeout(() => {
      if (isHoveringContentRef.current === true) {
        // Sometimes the mouse leave event on the dropdown trigger element is
        // sent after the mouse enter event on the opened content. In that scenario
        // we can ignore the trigger mouse leave event if we are already
        // hovering the dropdown content by that time.
        return
      }
      onHide()
    }, closeDelay)
  }

  const handleContentMouseEnter = () => {
    isHoveringContentRef.current = true
    // Prevent the dropdown from closing while hovering the content
    if (closeTimeout.current !== null) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }

  const handleContentMouseLeave = () => {
    isHoveringContentRef.current = false
    // Here the timeout allows the user to go back from the content to the trigger
    // without closing and reopening the dropdown immediately.
    closeTimeout.current = setTimeout(() => {
      onHide()
    }, closeDelay)
  }

  const clearAllTimeouts = () => {
    if (openTimeout.current !== null) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }
    if (closeTimeout.current !== null) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }

  useEffect(() => {
    return () => {
      clearAllTimeouts()
    }
  }, [])

  return {
    isHoverEnabled,
    handleTriggerMouseEnter,
    handleTriggerMouseLeave,
    handleContentMouseEnter,
    handleContentMouseLeave,
    clearAllTimeouts,
  }
}

function useParseShowOnHoverConfig(showOnHover: ShowOnHoverConfig): {
  isHoverEnabled: boolean
  openDelay: number
  closeDelay: number
} {
  return useMemo(() => {
    if (typeof showOnHover === 'boolean') {
      return {
        isHoverEnabled: showOnHover,
        openDelay: DEFAULT_OPEN_DELAY,
        closeDelay: DEFAULT_CLOSE_DELAY,
      }
    }

    return {
      isHoverEnabled: true,
      openDelay: showOnHover.openDelay ?? DEFAULT_OPEN_DELAY,
      closeDelay: showOnHover.closeDelay ?? DEFAULT_CLOSE_DELAY,
    }
  }, [showOnHover])
}
